import { useState } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { mfaSetup, mfaEnable, mfaDisable, mfaRegenerateBackupCodes } from "@/lib/apiClient";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { ShieldCheck, ShieldOff, Copy, Download, Lock, CheckCircle2 } from "lucide-react";

const inputCls = "w-full px-4 py-2.5 rounded-xl border border-[#E7E0D2] bg-[#FDFBF7] text-sm text-[#1F2937] focus:outline-none focus:border-[#064E3B] transition placeholder:text-[#9CA3AF]";

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-[#4B5563] mb-2">
        <Icon className="w-3.5 h-3.5" /> {label}
      </label>
      {children}
    </div>
  );
}

function BackupCodesGrid({ codes }) {
  const { t } = useI18n();
  const copyAll = () => {
    navigator.clipboard.writeText(codes.join("\n"));
    toast.success(t("mfa.toast_codes_copied"));
  };
  const download = () => {
    const blob = new Blob([codes.join("\n") + "\n"], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "quran-fluency-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="grid grid-cols-2 gap-2 p-4 rounded-xl bg-[#FDFBF7] border border-[#E7E0D2] font-mono text-sm text-[#1F2937]">
        {codes.map((c) => <div key={c}>{c}</div>)}
      </div>
      <div className="flex gap-3 mt-3">
        <button type="button" onClick={copyAll} className="inline-flex items-center gap-1.5 text-xs font-medium text-[#064E3B] hover:underline">
          <Copy className="w-3.5 h-3.5" /> {t("mfa.copy_all")}
        </button>
        <button type="button" onClick={download} className="inline-flex items-center gap-1.5 text-xs font-medium text-[#064E3B] hover:underline">
          <Download className="w-3.5 h-3.5" /> {t("mfa.download_txt")}
        </button>
      </div>
    </div>
  );
}

/** Inline password + authenticator-code reauth form, used before disabling
 * MFA or regenerating backup codes — both are bypass-adjacent actions. */
function ReauthForm({ submitLabel, onCancel, onSubmit, loading }) {
  const { t } = useI18n();
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");

  const submit = (e) => {
    e.preventDefault();
    onSubmit(password, code);
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <Field label={t("profile.current_password")} icon={Lock}>
        <input type="password" required autoFocus value={password} onChange={(e) => setPassword(e.target.value)}
          placeholder={t("profile.current_password_placeholder")} className={inputCls} />
      </Field>
      <Field label={t("mfa.authenticator_or_backup_code")} icon={ShieldCheck}>
        <input type="text" required value={code} onChange={(e) => setCode(e.target.value)}
          placeholder={t("mfa.code_placeholder")} className={inputCls} />
      </Field>
      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-medium text-[#4B5563] hover:text-[#1F2937]">
          {t("mfa.cancel")}
        </button>
        <button type="submit" disabled={loading}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#064E3B] text-white text-sm font-medium hover:bg-[#047857] disabled:opacity-50 transition">
          {loading ? t("mfa.please_wait") : submitLabel}
        </button>
      </div>
    </form>
  );
}

export default function TwoFactorAuth() {
  const { t } = useI18n();
  const { user, setUser } = useAuth();
  const [view, setView] = useState("idle"); // idle | setup | backup-codes | disable | regenerate
  const [busy, setBusy] = useState(false);
  const [setupData, setSetupData] = useState(null); // { secret, otpauth_url }
  const [enableCode, setEnableCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [savedConfirmed, setSavedConfirmed] = useState(false);

  const reset = () => {
    setView("idle");
    setSetupData(null);
    setEnableCode("");
    setBackupCodes([]);
    setSavedConfirmed(false);
  };

  const startSetup = async () => {
    setBusy(true);
    try {
      const data = await mfaSetup();
      setSetupData(data);
      setView("setup");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("mfa.toast_setup_failed"));
    } finally { setBusy(false); }
  };

  const confirmEnable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await mfaEnable(enableCode);
      setBackupCodes(data.backup_codes);
      setUser({ ...user, mfa_enabled: true });
      setView("backup-codes");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.toast_invalid_code"));
    } finally { setBusy(false); }
  };

  const disable2fa = async (password, code) => {
    setBusy(true);
    try {
      await mfaDisable(password, code);
      setUser({ ...user, mfa_enabled: false });
      toast.success(t("mfa.toast_disabled"));
      reset();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("mfa.toast_disable_failed"));
    } finally { setBusy(false); }
  };

  const regenerateCodes = async (password, code) => {
    setBusy(true);
    try {
      const data = await mfaRegenerateBackupCodes(password, code);
      setBackupCodes(data.backup_codes);
      setSavedConfirmed(false);
      setView("backup-codes");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("mfa.toast_regenerate_failed"));
    } finally { setBusy(false); }
  };

  return (
    <section className="mt-6 rounded-3xl bg-white border border-[#E7E0D2] p-6 sm:p-8" data-testid="two-factor-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-lg font-semibold text-[#1F2937]">{t("mfa.title")}</h2>
          <p className="text-sm text-[#4B5563] mt-1">
            {t("mfa.subtitle")}
          </p>
        </div>
        {view === "idle" && (
          <span className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${user?.mfa_enabled ? "bg-[#ECFDF5] text-[#064E3B]" : "bg-[#F3F4F6] text-[#4B5563]"}`}>
            {user?.mfa_enabled ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldOff className="w-3.5 h-3.5" />}
            {user?.mfa_enabled ? t("mfa.enabled") : t("mfa.disabled")}
          </span>
        )}
      </div>

      {view === "idle" && (
        <div className="mt-5">
          {user?.mfa_enabled ? (
            <div className="flex flex-wrap gap-3">
              <button data-testid="mfa-regenerate-btn" onClick={() => setView("regenerate")}
                className="px-4 py-2 rounded-full border border-[#E7E0D2] text-sm font-medium text-[#1F2937] hover:bg-[#F9F5EE] transition">
                {t("mfa.regenerate_codes")}
              </button>
              <button data-testid="mfa-disable-btn" onClick={() => setView("disable")}
                className="px-4 py-2 rounded-full border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50 transition">
                {t("mfa.disable_2fa")}
              </button>
            </div>
          ) : (
            <button data-testid="mfa-enable-btn" onClick={startSetup} disabled={busy}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#064E3B] text-white text-sm font-medium hover:bg-[#047857] disabled:opacity-50 transition">
              <ShieldCheck className="w-4 h-4" /> {busy ? t("mfa.starting") : t("mfa.enable_2fa")}
            </button>
          )}
        </div>
      )}

      {view === "setup" && setupData && (
        <div className="mt-5 space-y-5">
          <div className="flex flex-col sm:flex-row gap-6 items-start">
            <div className="p-3 bg-white rounded-xl border border-[#E7E0D2] shrink-0">
              <QRCodeSVG value={setupData.otpauth_url} size={160} marginSize={0} />
            </div>
            <div className="flex-1 text-sm text-[#4B5563] space-y-2">
              <p>{t("mfa.scan_qr")}</p>
              <div className="px-3 py-2 rounded-lg bg-[#FDFBF7] border border-[#E7E0D2] font-mono text-xs text-[#1F2937] break-all">
                {setupData.secret}
              </div>
            </div>
          </div>

          <form onSubmit={confirmEnable} className="space-y-4">
            <Field label={t("mfa.enter_6_digit_code")} icon={ShieldCheck}>
              <InputOTP maxLength={6} value={enableCode} onChange={setEnableCode}>
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot key={i} index={i} className="bg-[#FDFBF7] border-[#E7E0D2] h-11 w-11 text-base" />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </Field>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={reset} className="px-4 py-2 text-sm font-medium text-[#4B5563] hover:text-[#1F2937]">
                {t("mfa.cancel")}
              </button>
              <button data-testid="mfa-verify-enable-btn" type="submit" disabled={busy || enableCode.length !== 6}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#064E3B] text-white text-sm font-medium hover:bg-[#047857] disabled:opacity-50 transition">
                {busy ? t("mfa.verifying") : t("mfa.verify_and_enable")}
              </button>
            </div>
          </form>
        </div>
      )}

      {view === "backup-codes" && (
        <div className="mt-5 space-y-4">
          <p className="text-sm text-[#4B5563]">
            {t("mfa.save_codes_hint")}
          </p>
          <BackupCodesGrid codes={backupCodes} />
          <label className="flex items-center gap-2 text-sm text-[#1F2937]">
            <input type="checkbox" checked={savedConfirmed} onChange={(e) => setSavedConfirmed(e.target.checked)} />
            {t("mfa.saved_confirmed")}
          </label>
          <div className="flex justify-end">
            <button data-testid="mfa-backup-codes-done-btn" disabled={!savedConfirmed} onClick={reset}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#064E3B] text-white text-sm font-medium hover:bg-[#047857] disabled:opacity-50 transition">
              <CheckCircle2 className="w-4 h-4" /> {t("mfa.done")}
            </button>
          </div>
        </div>
      )}

      {view === "disable" && (
        <div className="mt-5">
          <p className="text-sm text-[#4B5563] mb-4">{t("mfa.confirm_disable_hint")}</p>
          <ReauthForm submitLabel={t("mfa.disable_2fa")} loading={busy} onCancel={reset} onSubmit={disable2fa} />
        </div>
      )}

      {view === "regenerate" && (
        <div className="mt-5">
          <p className="text-sm text-[#4B5563] mb-4">{t("mfa.confirm_regenerate_hint")}</p>
          <ReauthForm submitLabel={t("mfa.regenerate_codes_short")} loading={busy} onCancel={reset} onSubmit={regenerateCodes} />
        </div>
      )}
    </section>
  );
}
